<template>
  <div
    class="vehicles-preview-wrapper"
  >
    <text-field
      v-if="showInput"
      v-model="selectedVehicleNames"
      :field="displayFieldConfigMerged"
      :use-field-label="!!displayFieldConfig.useFieldLabel"
      readonly
      :edit-mode="editMode"
      :create-mode="createMode"
      :disabled="disabled"
      @click="openDialog"
      @prepend-inner-click="openDialog"
    />

    <!--    chosen vehicles - preview (in live tracking map options)-->
    <v-card
      v-if="selectedVehicles.length && showList"
      class="selected-vehicles-card"
      :class="{ 'selected-vehicles-card--scrollable': scrollableList }"
    >
      <v-treeview
        :items="vehicleTreeForDisplayList"
        hoverable
        activatable
        open-on-click
      >
        <template
          #prepend="{ item }"
        >
          <v-icon v-if="item.children && item.children.length > 0">
            {{ 'mdi-car' }}
          </v-icon>
          <v-icon
            v-else
            @click="$emit('list-vehicle-label-clicked', item)"
          >
            {{ 'mdi-car' }}
          </v-icon>
        </template>
        <template
          #label="{ item }"
        >
          <div
            @click="$emit('list-vehicle-label-clicked', item)"
          >
            <div>
              <div
                v-if="String(item.id).includes('group-')"
                class="preview-treeview-title"
              >
                {{ item.label }}
              </div>
              <div
                v-else
                class="preview-treeview-subtitle"
              >
                {{ item.garage_number ? item.garage_number + ',\u00A0' : '' }}
                {{ item.registration || '' }}
                {{ '(' + item.label + ')' }}
              </div>
            </div>
            <span
              v-if="item.driver"
              class="flex-fill preview-treeview-driver"
            >
              - {{ item.driver }}
            </span>
          </div>
        </template>
        <template
          #append="{ item }"
        >
          <v-btn
            icon
            @click="removeItem(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-treeview>
    </v-card>

    <!--    vehicles picker - dialog-->
    <v-dialog
      v-model="dialog"
      max-width="35em"
      :transition="false"
      :fullscreen="isMobile"
      @keydown.enter.prevent="closeDialog"
      @keydown.esc.prevent="closeDialog"
      @click:outside="closeDialog"
    >
      <v-card
        style="max-height: 90vh; display: flex; flex-direction: column;"
      >
        <v-card-title
          class="v-card-title"
        >
          {{ title }}
          <v-spacer />

          <div
            class="x-image"
          >
            <image-field
              src="/img/icons/x-icon-image.svg"
              contain
              width="30"
              height="30"
              @onClick="closeDialog"
            />
          </div>
        </v-card-title>

        <v-card-text
          style="overflow-y: auto; flex-grow: 1;"
        >
          <!--          search and tags field-->
          <v-row
            class="margin-0 search-tags-row"
          >
            <v-col
              class="padding-left-0 padding-right-0 search-col"
              :cols="isMobile ? 12 : 8"
            >
              <text-field
                v-model="search"
                :field="{
                  placeholder: $t('fleet.find_vehicle'),
                  prependInnerIcon: 'mdi-magnify',
                  clearable: true
                }"
                create-mode
                :autofocus="dialog"
              />
            </v-col>

            <v-col
              :cols="isMobile ? 12 : 4"
              class="padding-left-3 padding-right-0 tags-col"
            >
              <checkbox-list-menu
                :items="tags"
                :menu-title="$t('fleet.vehicle_tags')"
                @change="onTagsSelection"
              />
            </v-col>
          </v-row>

          <!--          selected tags-->
          <v-row
            v-if="selectedTags.length"
            class="tags-container"
          >
            <template
              v-for="item in selectedTags"
            >
              <v-chip
                :key="item.id"
                :value="item.value"
                color="lighten-3"
                class="ma-1"
                active-class="primary--text"
                close-icon="mdi-close"
                close
                small
                filter
                @click:close="onTagsSelection({ item, val: false })"
              >
                {{ item.text }}
              </v-chip>
            </template>
          </v-row>

          <!--          select all checkbox-->
          <v-row
            v-if="!maxSelect && visibleLeaves.length"
            class="mb-3"
          >
            <v-col class="py-0">
              <checkbox-field
                v-model="selectAll"
                class="select-all-checkbox"
                :config="{
                  label: $t('base.select_all'),
                  indeterminate: isSelectionPartial
                }"
              />
            </v-col>
          </v-row>

          <!--          vehicles selection-->
          <!-- Every checkbox here is ours. Vuetify's own selection was the source of a
               long tail of problems (a group's state was indistinguishable from one
               that merely followed its vehicles, and its internal cache raced with our
               updates), and its checkbox cannot be wrapped in a tooltip. The tree is
               left to do what it is good at: structure, filtering and expanding. -->
          <v-treeview
            :items="vehicleTree"
            :item-text="vehicleIdentifier"
            item-key="id"
            return-object
            open-on-click
            activatable
            :search="treeSearch"
            :filter="treeFilter"
            :active="active"
            :open.sync="open"
            @update:active="selectOnActive"
          >
            <template
              #label="{ item }"
            >
              <span
                class="node-row"
                :class="{ 'node-row--locked': isLabelLocked(item) }"
              >
                <!-- `v-tooltip` renders into the body, so the scrolling dialog cannot
                     clip it - a CSS one on the checkbox was cut off by the label's
                     own `overflow: hidden`. -->
                <v-tooltip
                  top
                  content-class="selection-limit-tooltip"
                  :disabled="!isNodeLocked(item)"
                >
                  <template
                    #activator="{ on }"
                  >
                    <span
                      class="node-checkbox"
                      :class="{ 'node-checkbox--disabled': isNodeLocked(item) }"
                      v-on="on"
                      @click.stop.prevent="onCheckboxClick(item)"
                    >
                      <v-icon
                        :color="checkboxColor(item)"
                      >
                        {{ checkboxIcon(item) }}
                      </v-icon>
                    </span>
                  </template>

                  {{ selectionLimitMessage }}
                </v-tooltip>

                <span
                  v-if="item.garage_number"
                  class="vehicle-group-label"
                >
                  {{ item.garage_number + ',\u00A0' }}
                </span>
                <span
                  v-if="item.registration"
                  class="vehicle-group-label"
                >
                  {{ item.registration }}
                </span>
                <span
                  class="vehicle-group-label"
                >
                  {{ item.registration ? '(' + item.label + ')' : item.label }}
                </span>
                <span
                  v-if="item.driver"
                  class="vehicle-group-label"
                >
                  - {{ item.driver }}
                </span>
              </span>
            </template>
          </v-treeview>
        </v-card-text>

        <v-card-actions>
          <universal-button
            type="base"
            :label="$t('base.ok')"
            style="width: 100%; color: #FFFFFF;"
            color="#65AD45"
            @click="onConfirmVehicleSelection"
          />
        </v-card-actions>
        <loader
          :show="controlsLoading"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { api } from '@/global/services/api'
import store from '@/global/store'
import CheckboxListMenu from '@/global/components/lists/CheckboxListMenu'
import { createNamespacedHelpers } from 'vuex'
import TextField from '@/global/components/fields/TextField.vue'
import CheckboxField from '@/global/components/fields/CheckboxField.vue'
import ImageField from '@/global/components/fields/ImageField.vue'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'
import Loader from '@/global/components/loader/Loader.vue'
import helperMixin from '../../../global/mixins/helperMixin'

const { mapGetters: mapGettersBase, mapActions: mapActionsBase } = createNamespacedHelpers('base/config')
const { mapGetters: mapGettersTracking, mapActions: mapActionsTracking } = createNamespacedHelpers('satellite-tracking/live-tracking')

const VEHICLE_IDENTIFIER = 'label'
export default {
  name: 'SatelliteTrackingVehiclesPreview',

  components: {
    Loader,
    UniversalButton,
    CheckboxField,
    ImageField,
    TextField,
    CheckboxListMenu
  },

  mixins: [
    helperMixin
  ],

  props: {
    workMachinesOnly: {
      type: Boolean,
      default: false
    },
    vehiclesOnly: {
      type: Boolean,
      default: false
    },
    fetchDataOnOpen: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: null
    },
    label: {
      type: String,
      default: function () {
        return this.$t('satellite-tracking/live-tracking.vehicles')
      }
    },
    trackersOnly: {
      type: Boolean,
      default: false
    },
    showList: {
      type: Boolean,
      default: false
    },
    scrollableList: {
      type: Boolean,
      default: false
    },
    showInput: {
      type: Boolean,
      default: true
    },
    showPicker: {
      type: Boolean,
      default: false
    },
    startOpen: {
      type: Boolean,
      default: false
    },
    maxSelect: {
      type: Number,
      default: null
    },
    scopeId: {
      type: Number,
      default: null
    },
    preselectedVehicles: {
      type: Array,
      default: () => ([])
    },
    value: {
      type: Array,
      default: () => ([])
    },
    showVehiclesWithAssignments: {
      type: Boolean,
      default: false
    },
    winterServiceBase: {
      type: [Number, String],
      default: null
    },
    canBusOnly: {
      type: Boolean,
      default: false
    },
    fuelProbesOnly: {
      type: Boolean,
      default: false
    },
    temperatureSensorsOnly: {
      type: Boolean,
      default: false
    },
    activeVehicles: {
      type: Boolean,
      default: false
    },
    activeTemperatureSensorsOnly: {
      type: Boolean,
      default: false
    },
    refreshVehicles: {
      type: Boolean,
      default: false
    },
    chosenTemperatureSensors: {
      type: Array,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    displayFieldConfig: {
      type: Object,
      default: () => ({})
    },
    disableOpen: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      dialog: false,
      search: null,
      active: [],
      vehicles: [],
      vehicleTree: [],
      selectedVehicles: [],
      vehicleIdentifier: VEHICLE_IDENTIFIER,
      tags: [],
      selectedTags: [],
      open: []
    }
  },

  computed: {
    ...mapGettersBase({
      isCompanyScopeUpdated: 'isCompanyScopeUpdated'
    }),
    ...mapGettersTracking({ controlsLoading: 'controlsLoading' }),

    displayFieldConfigMerged () {
      return {
        label: this.label,
        prependInnerIcon: 'mdi-car',
        placeholder: this.$t('satellite-tracking/tracking-history.choose_vehicles'),
        ...this.displayFieldConfig
      }
    },

    /**
     * The tree split into what can be picked and what only stands for a set of picks:
     * `leaves` are the selectable records (vehicles, or the temperature sensors under
     * them), `parents` are the group (and vehicle) nodes together with the leaves they
     * cover. `ancestorNames` carries the names of the nodes a leaf sits under, so a
     * search for a group name can be answered by its vehicles - the group node itself
     * must not match, see `matchesFilter`. Built in one pass so the selection computes
     * below stay cheap.
     */
    treeStructure () {
      const parents = []
      const leaves = new Map()
      const ancestorNames = new Map()

      const walk = (node, ancestors) => {
        if (!node.children?.length) {
          leaves.set(node.id, node)
          ancestorNames.set(node.id, ancestors)
          return [node]
        }

        const childAncestors = [...ancestors, node[this.vehicleIdentifier]].filter(Boolean)
        const childLeaves = node.children.flatMap(child => walk(child, childAncestors))
        parents.push({
          node,
          leaves: childLeaves
        })

        return childLeaves
      }

      this.vehicleTree.forEach(node => walk(node, []))

      return {
        parents,
        leaves,
        ancestorNames
      }
    },

    // What the search term and the picked tags currently keep. Only ever used to drive
    // what is shown around the tree (select all, which groups to open) - the tree
    // itself is handed every vehicle.
    visibleLeaves () {
      return [...this.treeStructure.leaves.values()].filter(leaf => this.matchesFilter(leaf))
    },

    // Groups holding at least one vehicle the filter keeps, so a search does not leave
    // its matches hidden inside collapsed groups.
    matchingGroups () {
      return this.treeStructure.parents
        .filter(({ leaves }) => leaves.some(leaf => this.matchesFilter(leaf)))
        .map(({ node }) => node)
    },

    selectionLimitMessage () {
      return this.maxSelect
        ? `${this.$t('satellite-tracking/history.selection_limit')} ${this.maxSelect}`
        : ''
    },

    /**
     * Once the limit is reached nothing more can be picked, so everything that would
     * only add more than the limit still has room for is switched off. A vehicle needs
     * one free place; a group needs one for each of its visible vehicles that is not
     * picked yet, so a group of fifteen is out of reach with five places left. A group
     * that is already picked in full stays enabled - ticking it off is how the user
     * makes room again, and so does a group holding only a part of what it shows, which
     * is why `isNodeLocked` tells the two apart.
     *
     * A limit of one is the exception: there the picker is a radio button, so a vehicle
     * always replaces the one held rather than being refused, and only a group of more
     * than one vehicle - which could never be taken on whole - is out of reach.
     */
    disabledNodeIds () {
      const disabled = new Set()

      if (!this.maxSelect) {
        return disabled
      }

      const selectedIds = new Set(this.selectedVehicles.map(item => item.id))
      const remainingPlaces = this.maxSelect - this.selectedVehicles.length
      // A limit of one is a radio button, not a full picker: a new pick takes over from
      // the one held, so a vehicle is never out of reach.
      const isSinglePick = this.maxSelect === 1

      this.treeStructure.leaves.forEach((leaf, id) => {
        if (!isSinglePick && !selectedIds.has(id) && remainingPlaces < 1) {
          disabled.add(id)
        }
      })

      this.treeStructure.parents.forEach(({ node, leaves }) => {
        const visibleLeaves = leaves.filter(leaf => this.matchesFilter(leaf))
        const isFullySelected = visibleLeaves.length > 0 &&
          visibleLeaves.every(leaf => selectedIds.has(leaf.id))

        if (isFullySelected) {
          return
        }

        // Ticking a group takes on all of the vehicles it shows at once, so what counts
        // is whether the whole group fits - not how much of it is still missing. With a
        // single place that is only true of a group holding one vehicle, and the pick
        // replaces whatever was held, so there are no free places to count.
        const isOutOfReach = isSinglePick
          ? visibleLeaves.length > 1
          : visibleLeaves.filter(leaf => !selectedIds.has(leaf.id)).length > remainingPlaces

        if (isOutOfReach) {
          disabled.add(node.id)
        }
      })

      return disabled
    },

    /**
     * VTreeview only filters while `search` is a non-empty string, and only recomputes
     * that filtering when the string itself changes - so the picked tags have to be
     * part of it even though the filter function reads them off the state.
     */
    treeSearch () {
      const search = this.search?.trim() || ''

      if (!this.selectedTags.length) {
        return search
      }

      return `${search}|tags:${this.selectedTags.map(tag => tag.id).join(',')}`
    },

    // VTreeview's signature is (item, search, textKey); the search string it hands over
    // carries the tag part too, so the terms are read off the state instead.
    treeFilter () {
      return (item) => this.matchesFilter(item)
    },

    // Some, but not all, of what the filter shows is picked - what the select all
    // checkbox reads as its partially selected state.
    isSelectionPartial () {
      const selectedIds = new Set(this.selectedVehicles.map(item => item.id))
      const selectedCount = this.visibleLeaves.filter(leaf => selectedIds.has(leaf.id)).length

      return selectedCount > 0 && selectedCount < this.visibleLeaves.length
    },

    selectAll: {
      get () {
        return this.visibleLeaves.length > 0 &&
          this.visibleLeaves.every(leaf => this.selectedVehicles.some(selected => selected.id === leaf.id))
      },
      // Select all applies to what the filter currently shows, and leaves whatever was
      // selected outside of it untouched.
      set (value) {
        const visibleIds = new Set(this.visibleLeaves.map(leaf => leaf.id))
        const keptSelection = this.selectedVehicles.filter(item => !visibleIds.has(item.id))

        this.applySelection(value
          ? [...keptSelection, ...this.visibleLeaves]
          : keptSelection)
      }
    },

    // filter vehicle tree to show the list only for selected vehicles, also grouped
    vehicleTreeForDisplayList () {
      return this.buildVehicleTree(this.selectedVehicles)
    },

    // Display human-readable names instead of vehicle IDs
    selectedVehicleNames: {
      get () {
        return this.selectedVehicles
          .map(vehicle => `${vehicle.garage_number ? vehicle.garage_number + ', ' : ''}${vehicle.registration} (${vehicle[this.vehicleIdentifier]})`)
          .join(', ')
      },
      set (value) {
        this.$emit('input', value)
      }
    }
  },

  watch: {
    dialog: {
      immediate: true,
      handler (val) {
        if (!val) {
          return
        }

        if (this.fetchDataOnOpen) {
          this.getVehicles()
        }
      }
    },
    matchingGroups: {
      immediate: true,
      handler (matchingGroups) {
        // Only an active filter opens groups by itself, or its matches would sit
        // hidden inside collapsed ones. Everything else - which group is open while
        // picking vehicles - is left to the user.
        if (!this.search && !this.selectedTags.length) {
          return
        }

        this.open = matchingGroups
      }
    },

    value () {
      this.populateSelectedVehicles()
    },

    selectedVehicles (newValue) {
      if (this.maxSelect && newValue.length > this.maxSelect) {
        if (this.maxSelect === 1) {
          this.selectedVehicles.shift()
        }
        else {
          this.selectedVehicles = this.selectedVehicles.slice(0, this.maxSelect)
          store.dispatch('base/notifications/push', this.$t('satellite-tracking/history.selection_limit') + ' ' + this.maxSelect)
        }

        // The trimmed selection is what the component holds from here on - without this
        // the parent keeps the over-long list it was handed a moment ago.
        this.$emit('input', this.selectedVehicles)
        return
      }
      this.$emit('has-selected-vehicles', !!newValue.length)
    },

    scopeId: async function () {
      await this.resetVehicles()
    },

    isCompanyScopeUpdated: async function () {
      await this.resetVehicles()
      await this.unsetCompanyScopeUpdated()
    },

    activeTemperatureSensorsOnly () {
      this.resetVehicles()
    },

    refreshVehicles (refreshVehicles) {
      if (refreshVehicles) {
        this.resetVehicles()
        this.$emit('reset-refresh-vehicles')
      }
    },
    fuelProbesOnly (fuelProbesOnly) {
      if (fuelProbesOnly) {
        this.getVehicles()
      }
    },
    canBusOnly (canBusOnly) {
      if (canBusOnly) {
        this.getVehicles()
      }
    }
  },

  async created () {
    if (!this.fetchDataOnOpen) {
      await this.getVehicles()
    }
  },

  async mounted () {
    this.dialog = this.startOpen || this.showPicker
  },

  methods: {
    ...mapActionsBase(['unsetCompanyScopeUpdated']),
    ...mapActionsTracking(['setControlsLoading']),

    openDialog () {
      if (!this.disableOpen) {
        this.dialog = true
      }
    },

    /**
     * One checkbox handler for both kinds of node: a group stands for the vehicles
     * under it, a vehicle for itself. Letting a pick go is always allowed - only
     * taking one on can run into the limit, which is what `disabledNodeIds` covers.
     */
    onCheckboxClick (item) {
      if (this.isGroupNode(item)) {
        this.toggleGroup(item)
        return
      }

      this.toggleVehicle(item)
    },

    toggleVehicle (vehicle) {
      const isSelected = this.selectedVehicles.some(selected => selected.id === vehicle.id)

      if (isSelected) {
        this.applySelection(this.selectedVehicles.filter(selected => selected.id !== vehicle.id))
        return
      }

      if (this.isNodeDisabled(vehicle)) {
        return
      }

      this.addToSelection([vehicle])
    },

    /**
     * A group's checkbox reads as one of three things, and it does what it shows: an
     * empty box takes on the vehicles the filter currently shows, a ticked one and a
     * partly ticked one both let go of the ones it holds. The vehicles the filter
     * hides stay exactly as they are either way.
     *
     * The partly ticked box deliberately clears rather than fills. Filling would make
     * the same box mean two different things depending on how much room the limit has
     * left - clear where the rest of the group no longer fits, fill where it does -
     * and a box that empties on one click and completes itself on the next is not a
     * control the user can read. Clearing is also the way places are freed up once a
     * part of a large group is picked, so it is the half that has to keep working when
     * the limit is reached; topping a group up is still there, one vehicle at a time.
     */
    toggleGroup (group) {
      if (this.isNodeLocked(group)) {
        return
      }

      const visibleLeaves = this.visibleLeavesOfGroup(group)

      if (!visibleLeaves.length) {
        return
      }

      const selectedIds = new Set(this.selectedVehicles.map(item => item.id))
      const releasedIds = new Set(
        visibleLeaves.filter(leaf => selectedIds.has(leaf.id)).map(leaf => leaf.id)
      )

      if (releasedIds.size) {
        this.applySelection(this.selectedVehicles.filter(item => !releasedIds.has(item.id)))
        return
      }

      this.addToSelection(visibleLeaves)
    },

    /**
     * Takes new picks on. With a limit of one the picker is a radio button: the new pick
     * replaces the one held instead of being refused, which is how it behaved before the
     * checkboxes took over from VTreeview's own selection.
     */
    addToSelection (nodes) {
      if (this.maxSelect === 1) {
        this.applySelection(nodes.slice(-1))
        return
      }

      this.applySelection([...this.selectedVehicles, ...nodes])
    },

    isGroupNode (item) {
      return !!item?.children?.length
    },

    visibleLeavesOfGroup (group) {
      const entry = this.treeStructure.parents.find(({ node }) => node.id === group?.id)
      return (entry?.leaves || []).filter(leaf => this.matchesFilter(leaf))
    },

    // Whether the node can still take new picks on.
    isNodeDisabled (item) {
      return this.disabledNodeIds.has(item?.id)
    },

    /**
     * Whether clicking the node does nothing at all: it cannot take a pick on and holds
     * none to let go of. A group that is out of reach but holds a few of its vehicles is
     * not locked - ticking it off is what frees places - so it is neither greyed out nor
     * given the limit tooltip.
     */
    isNodeLocked (item) {
      if (!this.isNodeDisabled(item)) {
        return false
      }

      if (!this.isGroupNode(item)) {
        return true
      }

      const selectedIds = new Set(this.selectedVehicles.map(selected => selected.id))

      return !this.visibleLeavesOfGroup(item).some(leaf => selectedIds.has(leaf.id))
    },

    /**
     * Whether clicking the node's name does nothing either. A vehicle's name is the
     * second way to pick it, so it is as dead as its checkbox; a group's name still
     * opens and closes the group, so it stays clickable even while locked.
     */
    isLabelLocked (item) {
      return !this.isGroupNode(item) && this.isNodeLocked(item)
    },

    checkboxIcon (item) {
      const selectedIds = new Set(this.selectedVehicles.map(selected => selected.id))

      if (!this.isGroupNode(item)) {
        return selectedIds.has(item.id) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
      }

      const visibleLeaves = this.visibleLeavesOfGroup(item)
      const selectedCount = visibleLeaves.filter(leaf => selectedIds.has(leaf.id)).length

      if (visibleLeaves.length && selectedCount === visibleLeaves.length) {
        return 'mdi-checkbox-marked'
      }

      return selectedCount ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline'
    },

    // An empty box keeps the default colour; a locked one is greyed by its class.
    checkboxColor (item) {
      if (this.isNodeLocked(item)) {
        return undefined
      }

      return this.checkboxIcon(item) === 'mdi-checkbox-blank-outline'
        ? undefined
        : '#65AD45'
    },

    /**
     * The one way the selection is changed. Every pick - a checkbox, select all, a
     * click on a vehicle's name, removing one from the list - lands here, so the
     * parent (and with it the map) hears about all of them the same way.
     */
    applySelection (selection) {
      // Keyed by id, so the same vehicle can never sit in the selection twice and eat
      // a place the limit still counts as free.
      const uniqueSelection = new Map((selection || []).map(item => [item.id, item]))

      this.selectedVehicles = [...uniqueSelection.values()]
      this.$emit('input', this.selectedVehicles)
    },

    /**
     * Whether an item survives the search term and the picked tags. A group carries no
     * tags of its own - it is kept by its children, which VTreeview checks separately.
     * It must never match on its own: a group holds none of the searched fields, so it
     * would match on the `undefined` they read as, and VTreeview stops filtering the
     * children of a node that matched - dragging the whole group along with it. Its name
     * is still searchable, as part of what its vehicles are matched on.
     */
    matchesFilter (item) {
      if (this.isGroupNode(item)) {
        return false
      }

      const search = this.search?.toLowerCase()?.trim() || ''
      // Only the parts the record actually has: an absent driver used to be read as the
      // string "undefined", which any search for one of its letters then matched.
      const itemName = [
        item.garage_number,
        item.registration,
        item[this.vehicleIdentifier] ? `(${item[this.vehicleIdentifier]})` : '',
        item.driver ? `- ${item.driver}` : '',
        ...(this.treeStructure.ancestorNames.get(item.id) || [])
      ].filter(Boolean).join(' ')
      const matchesSearch = itemName.toLowerCase().includes(search)

      if (!this.selectedTags.length) {
        return matchesSearch
      }

      return item.tags
        ? matchesSearch && this.elementContainsTag(this.selectedTags, item)
        : false
    },

    onTagsSelection (event) {
      const { item, val } = event || {}

      this.tags = this.tags.map(tag => {
        return {
          ...tag,
          value: tag.id === item.id ? !!val : !!tag.value
        }
      })

      if (val) {
        this.selectedTags.push({
          ...item,
          value: true
        })
      }
      else {
        const removeTagIndex = this.selectedTags.findIndex(tag => tag.id === item.id)
        this.selectedTags.splice(removeTagIndex, 1)
      }
    },

    onConfirmVehicleSelection () {
      this.$emit('selection-confirmation')
      this.closeDialog()
    },

    closeDialog () {
      this.dialog = false
    },

    async resetVehicles () {
      this.applySelection([])
      await this.getVehicles()
    },

    elementContainsTag (selectedTags, element) {
      return selectedTags.some(tag => tag?.id && Object.keys(element.tags).includes(tag.id.toString()))
    },

    removeItem (item) {
      if (item.children) {
        const vehiclesToBeRemoveIds = item.children.map(vehicle => vehicle.id)
        this.applySelection(this.selectedVehicles.filter(vehicle => !vehiclesToBeRemoveIds.includes(vehicle.id)))
      }
      else {
        this.applySelection(this.selectedVehicles.filter(vehicle => vehicle.id !== item.id))
      }

      this.$emit('vehicles-updated')
    },

    async getVehicles () {
      const companyScopeId = this.scopeId
      this.setControlsLoading(true)
      const route = '/vehicles/picker-options'

      const searchParams = {}
      if (this.trackersOnly) searchParams.trackers_only = 1
      if (this.workMachinesOnly) searchParams.work_machines = 1
      if (this.vehiclesOnly) searchParams.vehicles_only = 1
      if (this.canBusOnly) searchParams.can_bus_vehicles = 1
      if (this.fuelProbesOnly) searchParams.fuel_probe_vehicles = 1
      if (this.temperatureSensorsOnly) searchParams.temperature_sensor_vehicles = 1
      if (this.activeTemperatureSensorsOnly) searchParams.active_temperature_sensor_vehicles = 1
      if (this.showVehiclesWithAssignments) searchParams.report_vehicles = 1
      if (this.activeVehicles) searchParams.active_vehicles = 1
      if (this.winterServiceBase) searchParams.winter_service_base = this.winterServiceBase

      const response = await api('fleet').get(
        route,
        {
          ...searchParams,
          ...companyScopeId && { company_scope_id: companyScopeId }
        }
      )
      this.vehicles = response.data.vehicles
      this.tags = response.data.tags

      this.$emit('fetch-input-types', response.data.input_types)

      if (this.preselectedVehicles && this.preselectedVehicles.length) {
        this.applySelection(this.vehicles.filter(vehicle => {
          return this.preselectedVehicles.includes(vehicle.id)
        }))
      }

      this.populateSelectedVehicles()
      this.vehicleTree = this.buildVehicleTree(this.vehicles)
      this.setControlsLoading(false)
    },

    // Goes through the list of vehicles and builds a group-based tree for the treeview component
    buildVehicleTree (vehicles) {
      const groups = []
      const independent = []

      for (const vehicle of vehicles) {
        if (this.activeTemperatureSensorsOnly) {
          vehicle.children = []
          vehicle.tracker?.active_temperature_sensors?.forEach(temperatureSensorAssignment => {
            vehicle.children.push({
              id: 'sensorId-' + temperatureSensorAssignment.temperature_sensor?.id,
              label: temperatureSensorAssignment.temperature_sensor?.name,
              registration: vehicle.registration,
              vehicleId: vehicle.id
            })
          })
        }
        if (vehicle.group) {
          let group = groups.find(group => group.id === 'group-' + vehicle.group.id)
          if (!group) {
            group = {
              id: 'group-' + vehicle.group.id,
              [this.vehicleIdentifier]: vehicle.group.name,
              children: []
            }
            groups.push(group)
          }
          group.children.push(vehicle)
        }
        else {
          independent.push(vehicle)
        }
      }
      return [
        ...groups,
        ...independent
      ]
    },

    selectOnActive ([vehicle]) {
      if (!vehicle) {
        return
      }

      // Don't actually allow the user to mark vehicles as active
      this.active = []

      // By id, never by reference: the selection can hold the record the parent handed
      // back rather than the one the tree is built from, and comparing objects would
      // then read an already picked vehicle as a new one - unpicking it would turn into
      // a second pick, refused by the limit and counted twice when it was not.
      const isSelected = this.selectedVehicles.some(selected => selected.id === vehicle.id)

      // Toggle the selected status of the activated vehicle. Letting go of one is
      // always allowed - only picking another one can run into the limit.
      if (isSelected) {
        this.applySelection(this.selectedVehicles.filter(selected => selected.id !== vehicle.id))
        return
      }

      // The name is the second way to pick a vehicle, so it behaves like the checkbox
      // beside it: while that one is switched off, clicking the name does nothing.
      if (this.isNodeDisabled(vehicle)) {
        return
      }

      this.addToSelection([vehicle])
    },

    populateSelectedVehicles () {
      if (!this.value) {
        this.selectedVehicles = []
      }
      else {
        if (this.activeTemperatureSensorsOnly && this.chosenTemperatureSensors) {
          this.selectedVehicles = this.chosenTemperatureSensors.map(data => ({
            id: 'sensorId-' + data.sensorId,
            label: this.vehicles.find(vehicle => vehicle.id === data.vehicleId)?.tracker?.active_temperature_sensors
              ?.find(assignment => assignment.ts_id === data.sensorId)?.temperature_sensor?.name,
            registration: this.vehicles.find(vehicle => vehicle.id === data.vehicleId)?.registration,
            vehicleId: data.vehicleId
          }))
        }
        else {
          const selectedIds = new Set(this.value.map(v => (typeof v === 'object' ? v.id : v)))
          this.selectedVehicles = this.vehicles.filter(vehicle => selectedIds.has(vehicle.id))
        }
      }
    }
  }
}
</script>

<style lang="scss">
.selection-limit-tooltip.v-tooltip__content {
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #111827;
  font-family: 'Satoshi', sans-serif;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  opacity: 1 !important;
}
</style>

<style scoped lang="scss">
.vehicles-preview-wrapper {
  position: relative;
  width: 100%;
  min-width: 0;
}

.tags-container {
  margin: 1rem 0.33rem 0.33rem 0.33rem;
  padding: 0.33rem 0.66rem;
  border: 1px solid #F0F0F0;
  background: #F8F8F8;
}

.node-row {
  display: inline-flex;
  align-items: center;
}

.node-checkbox {
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
  cursor: pointer;
}

/* A locked checkbox does nothing when clicked, so it should not look like it would -
   and neither should the name beside it, which VTreeview marks as clickable. */
.node-row--locked,
.node-checkbox--disabled {
  cursor: default;
}

.node-checkbox--disabled ::v-deep .v-icon {
  color: #D1D5DB !important;
}

.vehicle-group-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #111827;
}

.v-card-title {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500 !important;
  font-size: 17px !important;
  color: #111827 !important;
  border-bottom: 1px solid #E5E7EB;
}

.selected-vehicles-card--scrollable {
  display: block;
  min-height: 0;
  height: auto;
  max-height: 100%;
  width: 100%;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  margin-top: 8px;
}

.x-image:hover {
  cursor: pointer;
}

.preview-treeview-title {
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 500;
  font-size: 14px !important;
  line-height: 20px;
  color: #111827;
  letter-spacing: 0 !important;
}

.preview-treeview-subtitle {
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 500;
  font-size: 10px !important;
  line-height: 16px;
  color: #111827;
  letter-spacing: 0 !important;
}

.preview-treeview-driver {
  font-family: 'Satoshi', sans-serif !important;
  font-weight: 500;
  font-size: 10px !important;
  line-height: 16px;
  color: #6B7280;
  letter-spacing: 0 !important;
}

::v-deep {
  .select-all-checkbox.v-input--indeterminate .v-input--selection-controls__input .v-icon {
    color: #65AD45 !important;
  }

  .v-treeview-node__toggle {
    color: #6B7280;
  }

  .v-dialog {
    border-radius: 16px !important;
  }
}

@media (max-width: 600px) {
  .selected-vehicles-card--scrollable {
    max-height: calc(100vh - 220px);
  }

  .search-tags-row {
    flex-direction: column;
  }

  .tags-col {
    padding-left: 0;
    padding-top: 0;
  }

  ::v-deep {
    .v-dialog {
      border-radius: unset !important;
    }
  }
}

@media (min-width: 601px) {
  .selected-vehicles-card--scrollable {
    max-height: calc(100vh - 160px);
  }
}
</style>
